/**
 * SavedPlansModal 已儲存規劃 Modal
 *
 * 顯示使用者已儲存的規劃列表：
 * - 列表顯示
 * - 載入規劃
 * - 刪除規劃
 */

import { Modal } from '@/components/layout/Modal';
import { getSavedPlans, deleteSavedPlan, type SavedPlan } from '@/api/saved-plans-api';
import { Spinner } from '@/components/shared/ProgressIndicator';
import { confirm } from '@/components/layout/Modal';
import { escapeHtml, sanitizeInput } from '@/utils/security';
import { createLogger } from '@/utils/logger';

// 建立具名 logger
const logger = createLogger('SavedPlansModal');

// ========================================================================
// 型別定義
// ========================================================================

export interface SavedPlansModalOptions {
  /** 選擇規劃回呼 */
  onSelect?: (plan: SavedPlan) => void;
}

// ========================================================================
// SavedPlansModal 類別
// ========================================================================

export class SavedPlansModal {
  private modal: Modal;
  private options: SavedPlansModalOptions;
  private plans: SavedPlan[] = [];

  constructor(options: SavedPlansModalOptions = {}) {
    this.options = options;

    this.modal = new Modal('已儲存的規劃', {
      size: 'lg',
      closeOnEscape: true,
      closeOnOverlay: true,
    });
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  async open(): Promise<void> {
    this.modal.open();
    await this.loadPlans();
  }

  close(): void {
    this.modal.close();
  }

  destroy(): void {
    this.modal.destroy();
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  private async loadPlans(): Promise<void> {
    const body = this.modal.getBody();

    // 顯示載入中
    body.innerHTML = '';
    const spinner = new Spinner({ message: '載入中...' });
    spinner.mount(body);

    try {
      this.plans = await getSavedPlans();
      this.renderPlans();
    } catch (error) {
      logger.error('Failed to load saved plans:', error);
      body.innerHTML = `
        <div class="text-center py-8">
          <p class="text-red-600">載入失敗，請稍後再試</p>
        </div>
      `;
    }
  }

  private renderPlans(): void {
    const body = this.modal.getBody();

    if (this.plans.length === 0) {
      body.innerHTML = `
        <div class="text-center py-8">
          <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p class="text-gray-500">尚未儲存任何規劃</p>
        </div>
      `;
      return;
    }

    body.innerHTML = `
      <div class="space-y-3 max-h-96 overflow-y-auto">
        ${this.plans.map((plan) => this.renderPlanItem(plan)).join('')}
      </div>
    `;

    // 綁定事件
    this.bindPlanEvents(body);
  }

  private renderPlanItem(plan: SavedPlan): string {
    const date = new Date(plan.createdAt).toLocaleString('zh-TW');
    // 轉義 HTML 防止 XSS 攻擊
    const safeName = escapeHtml(plan.name || '未命名規劃');
    const safePlanId = escapeHtml(plan.id);

    // 🔒 安全性：驗證 deskCount 是數字類型,防止注入攻擊
    const deskCountDisplay = (typeof plan.deskCount === 'number' && plan.deskCount > 0)
      ? `<p class="text-xs text-gray-500">辦公桌：${plan.deskCount} 個</p>`
      : '';

    return `
      <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-plan-id="${safePlanId}">
        <div class="flex-1 min-w-0">
          <h4 class="text-sm font-medium text-gray-900 truncate">${safeName}</h4>
          <p class="text-xs text-gray-500 mt-1">${escapeHtml(date)}</p>
          ${deskCountDisplay}
        </div>
        <div class="flex items-center gap-2 ml-4">
          <button type="button" class="load-btn px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
            載入
          </button>
          <button type="button" class="delete-btn px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            刪除
          </button>
        </div>
      </div>
    `;
  }

  private bindPlanEvents(container: HTMLElement): void {
    const items = container.querySelectorAll('[data-plan-id]');

    items.forEach((item) => {
      const planId = item.getAttribute('data-plan-id');
      const plan = this.plans.find((p) => p.id === planId);

      if (!plan) return;

      const loadBtn = item.querySelector('.load-btn');
      const deleteBtn = item.querySelector('.delete-btn');

      loadBtn?.addEventListener('click', () => this.handleLoad(plan));
      deleteBtn?.addEventListener('click', () => this.handleDelete(plan));
    });
  }

  private handleLoad(plan: SavedPlan): void {
    this.options.onSelect?.(plan);
    this.close();
  }

  private async handleDelete(plan: SavedPlan): Promise<void> {
    // 轉義 HTML 防止 XSS 攻擊
    const safeName = escapeHtml(plan.name || '未命名規劃');
    const confirmed = await confirm(
      `確定要刪除「${safeName}」嗎？此操作無法復原。`,
      {
        title: '刪除規劃',
        confirmText: '刪除',
        confirmVariant: 'danger',
      }
    );

    if (confirmed) {
      try {
        await deleteSavedPlan(plan.id);
        this.plans = this.plans.filter((p) => p.id !== plan.id);
        this.renderPlans();
      } catch (error) {
        logger.error('Failed to delete plan:', error);
        alert('刪除失敗，請稍後再試');
      }
    }
  }
}

// ========================================================================
// SavePlanModal 儲存規劃 Modal
// ========================================================================

export type SavePlanMode = 'new' | 'loaded';

export interface SavePlanModalOptions {
  /** 儲存回呼（新規劃或另存） */
  onSave?: (name: string) => void;
  /** 覆蓋回呼 */
  onOverwrite?: (name: string) => void;
  /** 取得模式 */
  getMode?: () => SavePlanMode;
  /** 取得當前規劃名稱 */
  getCurrentPlanName?: () => string;
}

export class SavePlanModal {
  private modal: Modal;
  private options: SavePlanModalOptions;
  private input: HTMLInputElement | null = null;
  private mode: SavePlanMode = 'new';
  private currentPlanName: string = '';

  constructor(options: SavePlanModalOptions = {}) {
    this.options = options;

    this.modal = new Modal('儲存規劃', {
      size: 'sm',
      closeOnEscape: true,
      closeOnOverlay: false,
    });
  }

  open(): void {
    // 判斷模式
    this.mode = this.options.getMode?.() ?? 'new';

    // 取得當前規劃名稱
    this.currentPlanName = this.options.getCurrentPlanName?.() ?? '';

    // 設定內容
    this.setupContentForMode();

    this.modal.open();

    // 聚焦輸入框
    setTimeout(() => this.input?.focus(), 100);
  }

  close(): void {
    this.modal.close();
  }

  destroy(): void {
    this.modal.destroy();
  }

  private setupContentForMode(): void {
    const body = this.modal.getBody();

    // 根據模式設定輸入框的值
    const inputValue = this.mode === 'loaded' ? escapeHtml(this.currentPlanName) : '';
    const placeholder = this.mode === 'loaded' ? '修改規劃名稱' : '例如：辦公室A、3F 規劃';
    const hint = this.mode === 'loaded' ? '' : '<p class="text-xs text-gray-500 mt-1">留空將使用時間戳記作為名稱</p>';

    body.innerHTML = `
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">規劃名稱${this.mode === 'new' ? '（可選）' : ''}</label>
          <input
            type="text"
            value="${inputValue}"
            class="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
            placeholder="${placeholder}"
          >
          ${hint}
        </div>
      </div>
    `;

    this.input = body.querySelector('input');

    // 根據模式設定按鈕
    const footer = this.modal.getFooter();

    if (this.mode === 'new') {
      // 新規劃模式：只有「儲存規劃」按鈕
      footer.innerHTML = `
        <button type="button" class="cancel-btn px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          取消
        </button>
        <button type="button" class="save-btn px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
          儲存規劃
        </button>
      `;

      const cancelBtn = footer.querySelector('.cancel-btn');
      const saveBtn = footer.querySelector('.save-btn');

      cancelBtn?.addEventListener('click', () => this.close());
      saveBtn?.addEventListener('click', () => this.handleSave());
    } else {
      // 已載入規劃模式：「另存新規劃」和「覆蓋舊規劃」按鈕
      footer.innerHTML = `
        <button type="button" class="cancel-btn px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          取消
        </button>
        <button type="button" class="save-new-btn px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors">
          另存新規劃
        </button>
        <button type="button" class="overwrite-btn px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
          覆蓋舊規劃
        </button>
      `;

      const cancelBtn = footer.querySelector('.cancel-btn');
      const saveNewBtn = footer.querySelector('.save-new-btn');
      const overwriteBtn = footer.querySelector('.overwrite-btn');

      cancelBtn?.addEventListener('click', () => this.close());
      saveNewBtn?.addEventListener('click', () => this.handleSave());
      overwriteBtn?.addEventListener('click', () => this.handleOverwrite());
    }

    // Enter 鍵儲存
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        if (this.mode === 'new') {
          this.handleSave();
        } else {
          // 已載入模式下，Enter 預設為覆蓋
          this.handleOverwrite();
        }
      }
    });
  }

  private handleSave(): void {
    // 清理並驗證使用者輸入
    const rawName = this.input?.value.trim() || '';
    const name = rawName ? sanitizeInput(rawName, 100) : new Date().toLocaleString('zh-TW');
    this.options.onSave?.(name);
    this.close();
  }

  private async handleOverwrite(): Promise<void> {
    // 轉義 HTML 防止 XSS 攻擊
    const safeName = escapeHtml(this.currentPlanName || '未命名規劃');
    const confirmed = await confirm(
      `確定要覆蓋「${safeName}」嗎？原有規劃將被取代。`,
      {
        title: '覆蓋規劃',
        confirmText: '確定覆蓋',
        confirmVariant: 'primary',
      }
    );

    if (confirmed) {
      // 取得輸入的名稱（可能已修改）
      const rawName = this.input?.value.trim() || '';
      const name = rawName ? sanitizeInput(rawName, 100) : this.currentPlanName;
      this.options.onOverwrite?.(name);
      this.close();
    }
  }
}
